// An open editor follows the saves of its project (spec docs/specs/editor/live-sync-web.md). It
// reads what changed since the version it holds when the team's stream says a newer one was saved,
// when the stream resyncs (a save it skipped sends no frame), when the stream is live again and
// every 15 s while it is not, and when its caller asks (another tab of this browser saved, or a
// save met a 409). It hands the newer version to the draft's
// reducer, which merges it in or meets it as a conflict. One read at a time: an ask while one
// runs reads again once the version taken in has rendered.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { useProjectHead, useProjectResyncs } from "../../api/project-heads";
import { FALLBACK_REFETCH_MS } from "../../platform/jobs/refetch";
import { useStreamStatus } from "../../platform/stream-status";
import type { Held } from "./draft";
import { readChanges } from "./project";
import { applyChange } from "./sync";

export interface ProjectSync {
  /**
   * Reads what changed since the version held, and hands it on. `told`: a newer version is known
   * (another tab said so, or a save met a 409), so nothing saves until it is read.
   */
  catchUp: (told?: boolean) => Promise<void>;
  /** A newer version is known and being read: nothing saves meanwhile. */
  behind: boolean;
}

export interface SyncOptions {
  teamId: string;
  brandId: string;
  projectId: string;
  /** What the server holds, as this tab knows it. */
  held: Held;
  /** The project read whole, when the log cannot give every change; null when the read fails. */
  readWhole: () => Promise<Held | null>;
  /** The newer versions, oldest first, for the draft's reducer to take in (spec live-sync-web.md). */
  onNewer: (versions: readonly Held[]) => void;
}

/** A read's outcome: the newer versions, oldest first (none when nothing is newer), or failed. */
type Read = { versions: readonly Held[] } | "failed";

/** A failed read is tried again after 1 s, doubling to 15 s, while it keeps failing. A test makes it short. */
export const RETRY_MS = { first: 1000, most: FALLBACK_REFETCH_MS };

/**
 * The server's versions newer than `held`, oldest first, or "failed" when neither read answered.
 * What changed comes from the project's log, applied in turn to `held`, a version each (so the
 * draft's reducer can know this tab's own save among them); when the log cannot give it all, the
 * project is read whole, its one version, unless the editor has closed meanwhile.
 */
async function readNewer(options: SyncOptions, mounted: () => boolean): Promise<Read> {
  const { teamId, brandId, projectId, held } = options;
  const read = await readChanges(teamId, brandId, projectId, held.version).catch(() => undefined);
  if (read !== undefined && read.version <= held.version) return { versions: [] };
  if (read?.complete === true) {
    const versions: Held[] = [];
    let at = held;
    for (const change of read.changes) {
      at = { version: change.version, timeline: applyChange(at.timeline, change), name: change.name ?? at.name };
      versions.push(at);
    }
    return { versions };
  }
  if (!mounted()) return "failed";
  const whole = await options.readWhole();
  if (whole === null) return "failed";
  return { versions: whole.version > held.version ? [whole] : [] };
}

export function useProjectSync(options: SyncOptions): ProjectSync {
  const latest = useRef(options);
  useLayoutEffect(() => {
    latest.current = options;
  });
  const [behind, setBehind] = useState(false);
  const reading = useRef<Promise<void> | null>(null);
  // Asked again while a read ran, and whether that ask knew of a newer version.
  const again = useRef<{ told: boolean } | null>(null);
  // Rounds asked for while a read ran, each read once the version taken in has rendered.
  const [rounds, setRounds] = useState(0);
  // A failed read is tried again with a backoff, stream or not: a save it missed sends no other word.
  const failures = useRef(0);
  const retry = useRef<number | undefined>(undefined);
  // A closed editor starts no read and tries none again; a read in flight then finishes, its
  // versions left (decision 6).
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      window.clearTimeout(retry.current);
    };
  }, []);
  const catchUp = useCallback((told = false): Promise<void> => {
    if (!mounted.current) return Promise.resolve();
    if (told) setBehind(true);
    if (reading.current !== null) {
      again.current = { told: told || again.current?.told === true };
      return reading.current;
    }
    window.clearTimeout(retry.current);
    const run = (async () => {
      let failed = false;
      try {
        const read = await readNewer(latest.current, () => mounted.current);
        failed = read === "failed";
        if (mounted.current && read !== "failed" && read.versions.length > 0) latest.current.onNewer(read.versions);
      } finally {
        reading.current = null;
        setBehind(false);
        if (mounted.current) {
          if (failed) {
            failures.current += 1;
            const wait = Math.min(RETRY_MS.most, RETRY_MS.first * 2 ** (failures.current - 1));
            retry.current = window.setTimeout(() => void catchUpNow.current(told), wait);
          } else {
            failures.current = 0;
          }
          if (again.current !== null) setRounds((count) => count + 1);
        }
      }
    })();
    reading.current = run;
    return run;
  }, []);
  // The effects call it through a ref: it reads the latest options when it runs.
  const catchUpNow = useRef(catchUp);
  useEffect(() => {
    const asked = again.current;
    if (rounds === 0 || asked === null) return;
    again.current = null;
    void catchUpNow.current(asked.told);
  }, [rounds]);
  // The team's stream says each save of the project (spec live-sync-stream.md).
  const head = useProjectHead(options.teamId, options.projectId);
  useEffect(() => {
    if (head !== undefined && head > latest.current.held.version) void catchUpNow.current(true);
  }, [head]);
  // A resync of the stream may have skipped saves, which send no frame (spec live-sync-stream.md).
  const resyncs = useProjectResyncs(options.teamId);
  useEffect(() => {
    if (resyncs > 0) void catchUpNow.current();
  }, [resyncs]);
  // A stream that was down may have missed a save: read once it is live again, and every 15 s
  // while it is not, as a job's read is. These know of no newer version, so nothing waits on them.
  const stream = useStreamStatus();
  const wasLive = useRef(stream === "live");
  useEffect(() => {
    const live = stream === "live";
    if (live && !wasLive.current) void catchUpNow.current();
    wasLive.current = live;
    if (live || stream === undefined) return undefined;
    const timer = window.setInterval(() => void catchUpNow.current(), FALLBACK_REFETCH_MS);
    return () => window.clearInterval(timer);
  }, [stream]);
  return { catchUp, behind };
}
